import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Plus, ChefHat, CalendarClock } from 'lucide-react';
import ShiftChip from './ShiftChip';
import { fmtDateLong, fmtTime, hoursLabel, levelStyle, money } from '../../../lib/scheduleFormat';

// Below the desktop breakpoint the grid becomes a day-by-day list rather than
// a separate mobile screen.
const DayList = ({ week, onAddShift, onEditShift, onAddBooking, onEditBooking, onUsualHours }) => (
  <div className="space-y-3" data-testid="week-day-list">
    {week.days.map((day) => {
      const style = levelStyle(day.coverage?.level);
      const shifts = week.rows.flatMap((row) =>
        row.shifts.filter((s) => s.date === day.date).map((s) => ({ ...s, rowName: row.name }))
      );
      return (
        <Card key={day.date} className={day.coverage ? style.card : ''} data-testid={`day-card-${day.date}`}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900">{fmtDateLong(day.date)}</p>
                <p className="text-xs text-gray-600">
                  {day.order_count} order{day.order_count === 1 ? '' : 's'} ·{' '}
                  {hoursLabel(day.booked_hours)} booked · {money(day.booked_cost)}
                </p>
              </div>
              {day.coverage && (
                <Badge variant="outline" className={style.badge} data-testid={`day-coverage-${day.date}`}>
                  {style.label}
                </Badge>
              )}
            </div>

            <div className="mt-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-700">
              <p className="font-medium text-slate-900">Kitchen access</p>
              {day.availability.length > 0 ? (
                day.availability.map((w, i) => (
                  <span key={i} className="mr-2">{fmtTime(w.start_time)}–{fmtTime(w.end_time)}</span>
                ))
              ) : (
                <span>None on this day</span>
              )}
            </div>

            <div className="mt-3 space-y-2">
              {day.bookings.map((booking) => (
                <button
                  key={booking.id}
                  type="button"
                  onClick={() => onEditBooking(booking, day)}
                  className="flex w-full items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900"
                  data-testid={`day-booking-${booking.id}`}
                >
                  <span>
                    <ChefHat className="mr-1 inline h-4 w-4" />
                    {fmtTime(booking.start_time)}–{fmtTime(booking.end_time)}
                  </span>
                  <span className="text-xs">{hoursLabel(booking.hours)} · {money(booking.cost)}</span>
                </button>
              ))}
              {day.availability.length > 0 && (
                <Button
                  variant="outline" className="h-11 w-full justify-center"
                  onClick={() => onAddBooking(day)}
                  data-testid={`day-add-booking-${day.date}`}
                >
                  <Plus className="mr-1 h-4 w-4" /> Book kitchen hours
                </Button>
              )}
              {day.availability.length > 0 && day.bookings.length === 0 && onUsualHours && (
                <Button
                  variant="ghost" className="h-11 w-full justify-center text-gray-600"
                  onClick={() => onUsualHours(day)}
                  data-testid={`day-usual-hours-${day.date}`}
                >
                  <CalendarClock className="mr-1 h-4 w-4" /> Book the usual hours
                </Button>
              )}
            </div>

            <div className="mt-3 space-y-2">
              {shifts.length === 0 && (
                <p className="text-sm text-gray-500" data-testid={`day-no-shifts-${day.date}`}>
                  Nobody scheduled yet.
                </p>
              )}
              {shifts.map((shift) => (
                <div key={shift.id}>
                  <p className="mb-1 text-xs font-medium text-gray-600">{shift.rowName}</p>
                  <ShiftChip shift={shift} onClick={onEditShift} />
                </div>
              ))}
              <Button
                variant="outline" className="h-11 w-full justify-center"
                onClick={() => onAddShift(day, null)}
                data-testid={`day-add-shift-${day.date}`}
              >
                <Plus className="mr-1 h-4 w-4" /> Add a shift
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

export default DayList;
